import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { hasValidConsumerSubscription } from "src/services/FeatureGatingService.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";

const INITIAL_DELAY_MS = 2000;
const POLLING_INTERVAL_MS = 3000;
const POLLING_TIMEOUT_MS = 20000;

/**
 * Hook that polls refetchAuth until the user has a valid consumer subscription.
 * Plain promise (not useMutation) so it settles even if the caller unmounts mid-poll.
 *
 * Flow:
 * 1. Waits 2s for webhook processing
 * 2. First refetchAuth attempt
 * 3. If no subscription, polls every 3s up to 20s total
 * 4. If still no subscription after timeout, logs error to Rollbar
 *
 * @returns {{ pollForSubscription: (context) => Promise<user>, isPolling: boolean }}
 *   context: { source, user_id, user_email, user_handle, stripeCustomerId, payment_type, trial_period, ...extra }
 */
export const useSubscriptionPolling = () => {
  const { refetchAuth } = useContext(AuthContext);
  const [isPolling, setIsPolling] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const pollForSubscription = useCallback(
    async (context = {}) => {
      const { source = "unknown", ...rollbarContext } = context;
      const startTime = Date.now();
      if (mountedRef.current) setIsPolling(true);

      try {
        // Initial delay to allow webhook processing
        await new Promise((resolve) => setTimeout(resolve, INITIAL_DELAY_MS));

        let updatedUser = await refetchAuth();
        if (hasValidConsumerSubscription(updatedUser)) {
          return updatedUser;
        }

        // Poll every POLLING_INTERVAL_MS until timeout
        while (Date.now() - startTime < POLLING_TIMEOUT_MS) {
          await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
          updatedUser = await refetchAuth();

          if (hasValidConsumerSubscription(updatedUser)) {
            return updatedUser;
          }
        }

        // Polling exhausted — report to Rollbar
        rollbar.error(`${source}: no valid subscription found after polling (${POLLING_TIMEOUT_MS / 1000}s)`, {
          ...rollbarContext,
          pollingDurationMs: Date.now() - startTime,
          consumerSubscription: updatedUser?.consumerSubscription,
        });

        return updatedUser;
      } finally {
        if (mountedRef.current) setIsPolling(false);
      }
    },
    [refetchAuth]
  );

  return { pollForSubscription, isPolling };
};
